/**
 * App Initialization
 *
 * This module handles the app initialization sequence.
 * Navigation is handled by the SplashScreen component after init completes.
 */

import { useStore } from "@/state/legacy_module_api";
import { initializeStateLayer } from "@/state/module_api";
import { SessionState } from "@/state/SessionState";
import { db } from "@/services/module_api";
import { crypto as switchCrypto, displayPhotoCachePaths, encodeBase64, decodeBase64, parseJwtPayload, setSigningKeyProvider } from "@/core_bridge/module_api";
import { appConfig } from "@/app/app-config";

/**
 * Generate NaCl key pair (Curve25519) using switch-core
 */
const generateKeyPair = () => {
  const kp = switchCrypto.boxKeypair();
  return {
    publicKey: kp.public_key,
    privateKey: kp.secret_key,
    publicKeyBase64: encodeBase64(kp.public_key),
    privateKeyBase64: encodeBase64(kp.secret_key),
  };
};

const generateMlKemKeyPair = () => {
  const kp = switchCrypto.mlKem768Keypair();
  return {
    publicKey: kp.public_key,
    secretKey: kp.secret_key,
    publicKeyBase64: encodeBase64(kp.public_key),
    secretKeyBase64: encodeBase64(kp.secret_key),
  };
};

const generateSigningKeyPair = () => {
  const kp = switchCrypto.signingKeypair();
  return {
    publicKeyBase64: encodeBase64(kp.public_key),
    secretKeyBase64: encodeBase64(kp.secret_key),
  };
};

/**
 * Initialize encryption key pair from database or generate new one
 */
export async function initKeyPair(): Promise<void> {
  const store = useStore.getState();

  const storedPub = await db.getPublicKey();
  const storedPriv = await db.getSecretKey();
  const appState = await db.getAppState();
  const storedMlKemPub = appState?.mlKemPublicKey || undefined;
  const storedMlKemPriv = appState?.mlKemSecretKey || undefined;
  let displayPhotoKey = await db.getDisplayPhotoKey();
  const coverPhotoKey = await db.getCoverPhotoKey();
  const profileJson = await db.getProfileJson();
  // Fallback to localStorage if DB key missing (e.g., prior storage init failure)
  if (!displayPhotoKey) {
    try {
      displayPhotoKey = localStorage.getItem("displayPhotoKey") || undefined;
    } catch {
      // ignore
    }
  }
  const localDisplayPhotoUrl = (() => {
    try {
      return localStorage.getItem("displayPhotoUrl") || undefined;
    } catch {
      return undefined;
    }
  })();

  // Ensure this device has an Ed25519 signing keypair. Additive like ML-KEM
  // above: a device provisioned before signing existed generates one now
  // without touching its identity keypair (device-key-immutability).
  if (!appState?.signingPublicKey || !appState?.signingSecretKey) {
    const signing = generateSigningKeyPair();
    await db.updateSigningKeyPair(signing.publicKeyBase64, signing.secretKeyBase64);
  }

  // Let core sign blob writes without importing the storage layer (which
  // imports from core). Reads fresh each call so a key generated above is
  // picked up without re-registering.
  setSigningKeyProvider(async () => {
    const state = await db.getAppState();
    if (!state?.signingPublicKey || !state?.signingSecretKey) return undefined;
    return { publicKey: state.signingPublicKey, secretKey: state.signingSecretKey };
  });

  store.setDisplayPhotoKey(displayPhotoKey || undefined);
  store.setCoverPhotoKey(coverPhotoKey || undefined);

  // Hydrate my own user record with cached profile info (for displayPhoto, etc.)
  try {
    if (profileJson) {
      const parsed = JSON.parse(profileJson);
      const userId = parsed?.userId || parsed?.user_id;
      const displayPhoto = parsed?.displayPhoto || parsed?.profilePhoto;
      const displayPhotoMedia = parsed?.displayPhotoMedia;
      const resolvedUrl = displayPhotoMedia?.url || displayPhoto || localDisplayPhotoUrl;
      const resolvedKey = displayPhotoMedia?.keyBase64 || displayPhotoKey || undefined;
      if (userId && resolvedUrl) {
        const cachePaths = displayPhotoCachePaths(
          userId,
          resolvedUrl
        ) as any;
        const current = store.contactsById?.[userId];
        const merged = {
          ...(current || {}),
          userId,
          displayPhoto: resolvedUrl,
          displayPhotoMedia: {
            url: resolvedUrl,
            keyBase64: resolvedKey,
            mimeType: displayPhotoMedia?.mimeType,
          },
        };
        store.setUser(merged as any);
        const cachePath = cachePaths?.user_cache_path;
        store.setMyProfilePhoto?.(cachePath || displayPhoto || localDisplayPhotoUrl);
        (store as any).setProfilePhoto?.({
          userId,
          url: resolvedUrl,
          keyBase64: resolvedKey,
          cachePath,
        });
      }
    }
  } catch (err) {
    console.warn("[init] failed to hydrate cached profile", err);
  }

  if (!storedPub || !storedPriv) {
    const kp = generateKeyPair();
    const mlKem = generateMlKemKeyPair();
    store.setKeyPair({
      cryptoKeys: { publicKey: kp.publicKey, secretKey: kp.privateKey },
      base64: { publicKey: kp.publicKeyBase64, secretKey: kp.privateKeyBase64 },
    });
    store.setMlKemKeyPair?.({
      cryptoKeys: { publicKey: mlKem.publicKey, secretKey: mlKem.secretKey },
      base64: { publicKey: mlKem.publicKeyBase64, secretKey: mlKem.secretKeyBase64 },
    });
    await db.updateKeyPair(
      kp.publicKeyBase64,
      kp.privateKeyBase64,
      mlKem.publicKeyBase64,
      mlKem.secretKeyBase64
    );
  } else {
    store.setKeyPair({
      cryptoKeys: {
        publicKey: decodeBase64(storedPub),
        secretKey: decodeBase64(storedPriv),
      },
      base64: { publicKey: storedPub, secretKey: storedPriv },
    });
    if (storedMlKemPub && storedMlKemPriv) {
      store.setMlKemKeyPair?.({
        cryptoKeys: {
          publicKey: decodeBase64(storedMlKemPub),
          secretKey: decodeBase64(storedMlKemPriv),
        },
        base64: { publicKey: storedMlKemPub, secretKey: storedMlKemPriv },
      });
    } else {
      const mlKem = generateMlKemKeyPair();
      store.setMlKemKeyPair?.({
        cryptoKeys: { publicKey: mlKem.publicKey, secretKey: mlKem.secretKey },
        base64: { publicKey: mlKem.publicKeyBase64, secretKey: mlKem.secretKeyBase64 },
      });
      await db.updateKeyPair(
        storedPub,
        storedPriv,
        mlKem.publicKeyBase64,
        mlKem.secretKeyBase64
      );
    }
  }
}

/**
 * Initialize app state (load token, hydrate users/groups/communities)
 */
export async function initAppState(): Promise<void> {
  const store = useStore.getState();
  const sessionState = new SessionState();
  const { userDirectory, avatarCache } = initializeStateLayer({ sessionState });

  try {
    await sessionState.initialize();
    await userDirectory.initialize();
    await avatarCache.initialize();
  } catch (err) {
    console.warn("[initAppState] failed to initialize state layer", err);
  }

  // Load app token
  const session_token = sessionState.getToken();
  if (session_token) {
    store.setApiToken(session_token);
  }

  // Hydrate users
  const users = await db.getUsers();
  if (users) {
    for (const user of users) {
      store.setUser(user);
    }
  }

  // Hydrate groups
  const grps = await db.getGrps();
  if (grps) {
    for (const grp of grps) {
      store.setGroup(grp);
    }
  }

  // Hydrate communities
  const comms = await db.getCommunities();
  if (comms) {
    for (const com of comms) {
      store.setCommunity(com);
    }
  }

  const state = useStore.getState();
  // Hydrate cached profile photos (prefer decrypted cache paths)
  try {
    const photos = (await (db as any).getProfilePhotos?.()) || {};
    const myUserId = state.apiToken
      ? parseJwtPayload(state.apiToken)?.user_id
      : undefined;
    for (const [userId, entry] of Object.entries(photos)) {
      const cachePath =
        (entry as any).cachePath ||
        (displayPhotoCachePaths as any)?.(userId, (entry as any).url)?.user_cache_path;
      (store as any).setProfilePhoto?.({
        ...(entry as object),
        userId,
        cachePath,
      });
      if (cachePath) {
        const contact = store.contactsById?.[userId];
        if (contact && store.setUser) {
          store.setUser({ ...contact, displayPhoto: cachePath } as any);
        }
        if (myUserId && userId === myUserId) {
          store.setMyProfilePhoto?.(cachePath);
        }
      }
    }
  } catch (err) {
    console.warn("[initAppState] failed to hydrate profile photo cache", err);
  }

  // Set Sentry user
  if (state.apiToken) {
    try {
      const jwt = parseJwtPayload(state.apiToken);
      if (jwt?.user_id) {
        window.Sentry?.setUser({
          id: jwt.user_id,
          appId: appConfig.appId,
        });
      }
    } catch (e) {
      console.warn("Failed to set Sentry user:", e);
    }
  }
}

/**
 * Initialize hilo codes from localStorage
 */
export function initHilo(): void {
  const store = useStore.getState();
  store.loadCodes();
}


/**
 * Sync auth token to service worker for authenticated push notification fetches
 */
export function syncTokenToServiceWorker(
  apiToken: string | undefined,
  contactsById: Record<string, any> = {}
): void {
  if (!("serviceWorker" in navigator)) return;

  const sendState = () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: "STATE_UPDATE",
        payload: {
          state: JSON.stringify({
            apiToken,
            apiUrl: appConfig.apiUrl,
            user: { contactsById },
          }),
        },
      });
      console.log("[init] Synced token to service worker");
    }
  };

  // Try immediately if controller exists
  if (navigator.serviceWorker.controller) {
    sendState();
  } else {
    // Wait for service worker to be ready
    navigator.serviceWorker.ready.then(() => {
      // Controller might still be null after ready, wait for controllerchange
      if (navigator.serviceWorker.controller) {
        sendState();
      } else {
        navigator.serviceWorker.addEventListener("controllerchange", sendState, { once: true });
      }
    });
  }
}
